/**
 * D1–D7's copy.
 *
 * ⚠️ THIS TABLE HAD A MALAYALAM COLUMN (ADR-044), and the layout decision it
 * forced is still here on purpose: the tables below are scroll-wrapped rather
 * than fixed-width. A long name or a long class label breaks a dense table the
 * same way a longer language did.
 */

export type Lang = "en";

export const TEACHER_COPY = {
  en: {
    // D1
    welcome: (name: string, className: string) =>
      `Welcome, ${name} · Class: ${className}`,
    /*
     * ⚠️ THE GREETING IS THE PAGE TITLE, AND THE CLASS IS NOT IN IT. The chrome
     * bar above already names the class; repeating it here made a teacher read
     * "Class 2B" twice within four lines. `welcome` is kept because the
     * component sheet renders it.
     */
    welcomeTitle: (name: string) => `Hello, ${name}`,
    modeHint: (className: string) =>
      `${className} · what would you like to do?`,
    playTitle: "Make children play",
    playBody: "Pick a child by roll number and hand over the laptop.",
    playAction: "Start a session",
    dashTitle: "Dashboard",
    dashBody: "Progress, leaderboard and NCF coverage for the class.",
    dashAction: "Open dashboard",

    // D2
    roster: "Roster",
    search: "Search by name or roll number",
    roll: "Roll",
    student: "Student",
    parent: "Parent",
    phone: "Phone",
    lastPlayed: "Last played",
    never: "Not started",
    play: "Play",
    noMatch: "No child matches that.",

    /* ── Play setup — the class code, and the register children sign in against ── */
    playSetupTitle: "Signing in to play",
    playSetupHint:
      "Children tap their class, type their roll number, then write their own name.",
    classCode: "Class code",
    /*
     * ⚠️ IT EXPLAINS WHY NOBODY CHOOSES THE CODE, because that is the question a
     * teacher asks first and the answer is not obvious. Letters a child cannot
     * tell apart are the failure this prevents.
     */
    classCodeWhy:
      "Write this on the board. It is made for young children to copy — it never contains O, 0, I, 1, L, S or 5. Type it once on each tablet; after that the class is a card the children tap.",
    rotate: "New code",
    rotateConfirm:
      "A new code takes effect at once, and every tablet still using the old one stops working until it is typed in again. Continue?",
    rotateYes: "Yes, make a new code",
    rotating: "Making a new code…",
    cancel: "Cancel",
    playAsChild: "Play as a child",
    signsInWith: "Signs in with",
    usesName: "Their own name",
    usesPassword: "A password you set",
    needsRoll: "Needs a roll number",
    noChildren: "No children on this register yet.",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",

    // D3
    confirmTitle: "Check before you start",
    pickGame: "Pick a game",
    /* ⚠️ The picker's state, in words as well as in colour — §12. */
    selected: "Selected",
    tapToSelect: "Tap to choose",
    launch: "Start",
    back: "Back",

    // D4
    overview: "Class overview",
    children: "Children",
    activeThisWeek: "Played this week",
    sessionsThisWeek: "Sessions this week",
    topOfClass: "Top of class",
    /*
     * ⚠️ THE FOUR TILE CAPTIONS, WHICH D4 USED TO SPELL OUT IN THE JSX. The
     * labels above were already here and already unused — the screen rendered
     * the literal string "Children" beside `t.children` sitting in this table —
     * so the tier had a copy file that four of its most-read words routed
     * around. Captions live here for the same reason labels do.
     */
    totalStudents: "Total students",
    studentsPlayed: "Students played",
    totalSessions: "Total sessions",
    /*
     * ── the two measures the row was missing, and the line under every tile ──
     *
     * 🔴 THE ROW SAID *HOW MUCH* AND NEVER *HOW WELL* OR *HOW LONG*. "21 played,
     * 83 sittings" describes a busy week and a week of children opening the game
     * and walking away in exactly the same words. Both of the new tiles answer a
     * question a teacher asks out loud on a Monday, and both of them are the kind
     * of number that is worthless without its denominator — so every caption
     * below carries one.
     */
    avgPlayTime: "Play time",
    avgAccuracy: "Accuracy",
    /*
     * ⚠️ "PER SITTING", NOT "PER SESSION". The whole tier says *sitting* for one
     * child's turn at a game — `activitySessions` above is "Sittings" — and a
     * stat row that switches to the API's word for the same thing is a teacher
     * wondering whether they are two different counts.
     */
    perSitting: "Per sitting",
    /* 🔴 THE MEAN'S DENOMINATOR, IN WORDS. A tablet closed mid-round never
       records an end, so the average covers fewer sittings than the class ran —
       and "32m" that silently describes 12 of 83 is the kind of figure that gets
       repeated at a parents' evening. */
    /* ⚠️ IT DOES NOT REPEAT THE WORD "SITTING" — the caption reads "Per sitting ·
       60 of 83 timed", and the noun is already in the first half. */
    timedOf: (timed: number, sessions: number) => `${timed} of ${sessions} timed`,
    /* ⚠️ NOT "no sittings" — the sittings happened, the clock did not. */
    noTimedSittings: "No sitting finished in the game",
    answeredOf: (correct: number, attempts: number) =>
      `${correct} right of ${attempts} answered`,
    /*
     * 🔴 A REFUSAL, AND IT IS THE HONEST ANSWER ON THE FIRST MONDAY OF A PILOT.
     * Three right out of four is not a 75 % class; printing it would put a
     * figure that swings twenty points per tap at the top of the screen.
     */
    tooFewAnswers: (floor: number) => `Fewer than ${floor} answers this week`,
    /* The register as the denominator of "played this week". */
    ofClass: (percent: number) => `${percent}% of the class`,

    /*
     * ── the delta line, which is what makes any of the above actionable ──
     *
     * ⚠️ SIGNED AND IN THE TILE'S OWN UNIT, NOT A PERCENTAGE CHANGE. *"↑12%"* on
     * 83 sittings is a sum a teacher has to undo before it means anything, and on
     * a small class it is worse than useless: 2 sittings to 3 reads "↑50%".
     */
    vsLastWeek: (amount: number) =>
      `${amount > 0 ? "+" : ""}${amount} vs last week`,
    vsLastWeekPoints: (amount: number) =>
      `${amount > 0 ? "+" : ""}${amount} points vs last week`,
    noChangeVsLastWeek: "Same as last week",
    /* 🔴 A CLASS THAT DID NOTHING LAST WEEK HAS NO BASELINE, NOT A BASELINE OF
       NOUGHT. "+11 vs last week" would frame a beginning as an improvement. */
    firstWeek: "Nothing to compare with yet",

    /*
     * ── what the class is learning, as opposed to how much it played ──
     *
     * 🔴 EVERY OTHER FIGURE ON D4 COUNTS ACTIVITY. A class can lead all six
     * tiles while never once opening anything but Sky Quest — the games panel
     * says so per GAME, and this says it per SKILL, which is the axis a week is
     * planned on and the one an NCF conversation happens in.
     */
    skillsTitle: "What this class is learning",
    /* ⚠️ THE HINT NAMES THE DENOMINATOR, because the bars are shares of the
       register and a bar is not a number anybody can read aloud. */
    skillsHint:
      "Children who have touched each skill, out of everyone on the register.",
    /* ⚠️ "Not started yet" — not "0 children". A skill nobody has opened is a
       thing to go and do, not a score of nought. */
    skillUntouched: "Not started yet",
    skillsNothingYet:
      "Nothing recorded against any skill yet. Start a play session to fill this in.",
    /* 🔴 THE READ'S OWN DENOMINATOR, SEPARATE FROM THE REGISTER. A class where
       four of twenty-seven have ever played has a skill picture drawn from four
       children, and every bar under it means less than it looks. */
    skillsBasis: (played: number, total: number) =>
      `Drawn from ${played} of ${total} children who have played at all`,
    /*
     * ── the class over time ──
     *
     * 🔴 THE LEGEND NAMES BOTH LINES BECAUSE THEY ARE NOT THE SAME COUNT. One
     * child playing five times and five children playing once are the same
     * number of sittings, and a teacher must be able to tell which she is
     * looking at without counting pixels.
     */
    activityTitle: "How often this class plays",
    activityHint: (days: number) => `Sittings on each of the last ${days} days.`,
    activitySessions: "Sittings",
    activityChildren: "Children who played",
    activityNothingYet: "No sittings in this window yet.",

    /*
     * ── the month register ──
     *
     * 🔴 THE WORDING NEVER SAYS "PRESENT", AND THAT IS THE WHOLE POINT. The
     * register stores absences only, so a day with no marks is a day nobody
     * took it, a holiday, or a day everyone was there — and the product cannot
     * tell which. Every string here is about ABSENCES or about nothing being
     * recorded; "all present" would be a claim the data cannot support.
     */
    monthTitle: "This month",
    monthHint:
      "Days with absences marked. A blank day means nothing was recorded — the register keeps absences only, so it cannot tell a holiday from a day everyone was present.",
    monthPrev: "Previous month",
    monthNext: "Next month",
    monthToday: "This month",
    monthNoMarks: "No absences recorded in this month.",
    monthAbsentOn: (n: number, day: string) =>
      `${n === 1 ? "1 child" : `${n} children`} absent on ${day}`,
    monthNothingOn: (day: string) => `Nothing recorded on ${day}`,
    /** Monday first — the school week the register is taken against. */
    monthWeekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    monthPerChild: "Absences per child",
    monthDaysAbsent: (n: number) => (n === 1 ? "1 day absent" : `${n} days absent`),
    monthNoneForChild: "None",

    /*
     * ── one day, opened ──
     *
     * 🔴 "PRESENT" IS QUALIFIED EVERY TIME IT APPEARS, because it is an
     * inference and not a record. The register stores absences only, so the
     * present list is "on the register today and not marked absent" — which
     * includes a child who joined after that day and omits one who has left.
     * `monthPresentBasis` is not a footnote to trim: without it the panel
     * states a roster the product never captured.
     */
    monthPickDay: "Open a day to see who was in.",
    monthDayAbsent: "Absent",
    monthDayPresent: "Present",
    monthDayCount: (n: number, of: number) => `${n} of ${of}`,
    monthPresentBasis:
      "“Present” means on the register today and not marked absent that day — the register keeps absences only, so a child who joined later is counted in and one who has left is missing.",
    monthDayNothing:
      "Nothing was recorded on this day. The register keeps absences only, so this could be a holiday, a day nobody took it, or a day everyone was present — and the product cannot tell which.",
    monthClose: "Close",
    /** The accessible name of a day button — a control with no name is unusable. */
    monthDayLabel: (day: string, n: number) =>
      `${day} — ${n === 1 ? "1 child absent" : `${n} children absent`}`,
    monthDayLabelBlank: (day: string) => `${day} — nothing recorded`,
    needsAttention: "Needs attention",
    notStarted: "Not started yet",
    nobodyNeedsAttention: "Nobody needs chasing today.",
    everyoneStarted: "Everyone has started.",
    /*
     * 🔴 THE THIRD CARD, AND THE ONE THAT MAKES D4 READABLE ON A GOOD DAY.
     * The screen had only the two lists above, so a class where twenty-three of
     * twenty-seven children are fine was drawn as two columns of problems and
     * no mention of the twenty-three.
     *
     * ⚠️ THE HEADING IS THE FRIENDLY WORD AND THE HINT IS THE HONEST
     * DEFINITION. The count is a subtraction — the register minus the two
     * lists — and the two lists are computed from recorded activity, so a child
     * could in principle be in neither for some reason other than doing well.
     * Saying "not on either list" is what the number actually means; saying
     * only "going well" would be a claim about children the product has not
     * looked at.
     */
    goingWell: "Going well",
    goingWellHint: "On the register and on neither list above.",
    goingWellBody: (total: number) =>
      total === 1
        ? "of 1 child on the register"
        : `of ${String(total)} children on the register`,
    /** ⚠️ The label on the priority cards' one action — it opens D5's list. */
    openStudents: "See all students",
    idle: (days: number) => `${days} days since last played`,
    lowAccuracy: (pct: number) => `${pct}% correct`,
    openChild: "Open",
    nobodyPlayedYet: "Nobody has played yet",

    // D5
    detail: "Student",
    insight: "Insight",
    strengths: "Strengths",
    focus: "Worth practising",
    recentEvents: "Recent activity",
    messageParent: "Message parent",
    /**
     * ✅ IT IS SWITCHED ON NOW, AND THIS STRING IS KEPT AS THE RECORD OF WHAT IT
     * USED TO SAY. Both tiers carried a disabled button and this sentence; the
     * thread exists, so the button opens it. The key stays because two screens
     * still reference it and deleting a copy key is a merge conflict in the one
     * place a reader is looking for an explanation.
     */
    messageParentDisabled: "Available once parent messaging is switched on",
    exportCsv: "Download CSV",
    exportCsvBusy: "Preparing…",
    /*
     * 🔴 THE BUTTON USED TO DO NOTHING AND SAY NOTHING, which is the one
     * outcome a teacher cannot tell apart from a slow download. It now fetches
     * the server's own file, so it can also fail — and a failed export has to
     * say so, because the alternative is a teacher waiting for a file that is
     * never coming.
     */
    exportCsvFailed: "The download did not start. Try again in a moment.",

    /*
     * ── messages ────────────────────────────────────────────────────────────
     *
     * 🔴 THE PRODUCT COULD SEND A FAMILY A PRE-APPROVED TEMPLATE AND NOTHING
     * ELSE. WhatsApp templates are one-way and their bodies are submitted to
     * Meta weeks in advance; *"could you send a spare set of clothes tomorrow"*
     * is not a template and never will be. Both tiers have shipped a disabled
     * button admitting it.
     */
    messages: "Messages",
    messagesHint: "Pick a child to open the conversation with their family.",
    messagesPick: "Choose a child",
    messagesEmpty:
      "No messages yet. Say something and it appears on their parent’s phone.",
    messagesNoParent:
      "No parent is linked to this child yet. What you write is kept and appears as soon as the school links one.",
    messageWrite: "Write a message",
    messageSend: "Send",
    messageTo: (who: string) => `To ${who}`,
    messageFrom: (who: string) => `From ${who}`,
    /** ⚠️ "Seen", never "read" — the server can only know the thread was opened. */
    messageSeen: "Seen",
    messageUnread: (n: number) => (n === 1 ? "1 new" : `${String(n)} new`),
    /*
     * 🔴 IT SAYS WHOSE TURN IT IS, NOT HOW MANY ARRIVED. "2 new" goes to zero
     * the moment the thread is opened, so a parent's question read at 09:05 and
     * meant to be answered after the lesson is indistinguishable by 09:30 from a
     * conversation that ended last week. This one survives reading and clears
     * only when the teacher actually replies.
     */
    messageAwaitingReply: "Waiting on you",
    messageNoneYet: "No messages",
    messageWaiting: "Waiting for a reply",
    /** The teacher's own last line, so the inbox reads as a conversation. */
    messageYou: "You",

    /*
     * ── class notices ───────────────────────────────────────────────────────
     *
     * ⚠️ "NOTICE", NOT "ANNOUNCEMENT", ON THIS TIER. The school-wide one is the
     * admin's and is called an announcement there; a teacher writing to her own
     * thirty families is putting something on the class notice board, and the
     * shorter word keeps the two apart on a screen where both could appear.
     */
    noticeTitle: "Send a notice to this class",
    noticeWhy:
      "Every family in this class sees it in the app. Tick the box to send it to their phones as well.",
    noticeHeadline: "Headline",
    noticeBody: "What you want to say",
    noticeWhatsapp: "Also send this as a WhatsApp message",
    noticeWhatsappHint:
      "Families who have opted in get one message with the headline and the first couple of lines.",
    noticePost: "Send to this class",
    noticePosted: "Sent.",
    noticeQueued: (n: number) =>
      n === 1
        ? "Sent, and 1 message queued."
        : `Sent, and ${String(n)} messages queued.`,
    noticeFinal:
      "A notice cannot be edited or taken back once it is sent — the messages have already gone. To correct one, send another.",
    noticeNone: "You have not sent this class anything yet.",
    noticeFailed: "That notice was not sent.",

    // D6
    leaderboard: "Leaderboard",
    coins: "Coins",
    stickers: "Stickers",
    streak: "Streak",
    topSkill: "Strongest",

    // D7
    reports: "Reports",
    coverageTitle: "NCF-FS coverage",
    covered: "Covered",
    uncovered: "Not yet covered",
    game: "Game",
    category: "Category",
    codes: "NCF-FS",
    events: "Activities",

    // Students — every class at once
    allStudents: "Students",
    allStudentsHint:
      "Every child you teach, by class and section. Open one to see their progress.",
    showingOf: (shown: number, total: number) =>
      `Showing ${shown} of ${total} children`,
    totalChildren: (children: number, classes: number) =>
      `${children} children across ${classes} classes`,

    // Sidebar
    navDashboard: "Dashboard",
    navStudents: "Students",
    navPlay: "Play mode",
    navLeaderboard: "Leaderboard",
    navReports: "Reports",
    navAttendance: "Attendance",
    /** 🔴 One word, like every other item: this bar is read at a glance mid-lesson. */
    navMessages: "Messages",

    /* ── T7 · the register ── */
    attendanceTitle: "Attendance",
    attendanceHint:
      "Mark each child present or absent. Nothing is sent until you save.",
    attendanceFor: (day: string) => `Today, ${day}`,
    /*
     * ── T7 · reading a register you may not take ──
     *
     * 🔴 A CLASS HAS ONE TEACHER OF RECORD, AND TAKING THE REGISTER MESSAGES
     * FAMILIES. A subject teacher can open this class and see who is in — that
     * is a fair question for somebody teaching the lesson — and the mark itself
     * belongs to the person a parent hears from. The server has always said so
     * (`AttendanceService.writableClass`); this is the sentence that stops the
     * refusal arriving as a failed save.
     *
     * ⚠️ IT NAMES THEM WHERE IT CAN. "Somebody else takes this register" is a
     * dead end; "Nishant takes this register" is a person to go and find.
     */
    registerReadOnly: (teacher: string | null) =>
      teacher === null
        ? "Only this class's own teacher can take its register. You can read it here."
        : `${teacher} takes this class's register. You can read it here.`,
    /** ⚠️ Replaces the count-and-save footer, which has nothing to offer here. */
    registerReadOnlyWhy:
      "Marking a child absent messages their family, so it stays with the one teacher a parent hears from.",
    /** The column heading when the two buttons become a plain status. */
    attendanceToday: "Today",
    /*
     * 🔴 "PRESENT" AND "ABSENT", NOT "HERE" AND "NOT HERE".
     *
     * The euphemism was the bug. A register is a formal record a school keeps
     * and an inspector reads, and every other register a teacher has ever
     * signed — paper, state portal, the school's own MIS — says PRESENT and
     * ABSENT. "Not here" also reads as a *report of the moment* rather than a
     * mark for the day, which is exactly the wrong idea on the one screen that
     * messages a parent. Two words, both of them the words already in use.
     */
    present: "Present",
    absent: "Absent",
    /**
     * 🔴 THE COLUMN IS NOT NAMED AFTER ONE OF ITS TWO ANSWERS. The header read
     * "Here" while its cells read "Here" or "Not here", so the column claimed
     * to be a fact and delivered a choice.
     */
    attendanceStatus: "Attendance",
    /** 🔴 The verb a teacher does, not a claim about the room. */
    markAllPresent: "Mark all present",
    markAllPresentDone: "All marked present",
    /**
     * 🔴 NAMES THE CHILD, because this is what a screen reader announces for a
     * row's two buttons and "Present" alone is the same on all twenty-one rows.
     */
    presenceFor: (name: string) => `Attendance for ${name}`,
    markPresent: (name: string) => `Mark ${name} present`,
    markAbsent: (name: string) => `Mark ${name} absent`,
    saveRegister: "Save the register",
    savingRegister: "Saving…",
    /* 🔴 The number that answers a teacher's real question after saving, which
       is not "did it save" but "do the parents know". */
    savedRegister: (absent: number, notified: number) =>
      absent === 0
        ? "Saved. Everyone is marked present today."
        : `Saved. ${absent} marked absent, and ${notified} ${
            notified === 1 ? "parent was" : "parents were"
          } messaged.`,
    saveRegisterFailed: "That register did not save.",
    /* ⚠️ Said on the row, because the fix is a roster linkage the school owns. */
    noParentLinked: "No parent to message",
    unreachableNote: (n: number) =>
      n === 1
        ? "1 child marked absent has no parent we can message. Marking them records it, but tells nobody."
        : `${n} children marked absent have no parent we can message. Marking them records it, but tells nobody.`,
    /**
     * 🔴 BOTH NUMBERS, BECAUSE THE REGISTER HAS TWO ANSWERS. "0 of 4 not here"
     * made a teacher subtract to learn the number she is actually asked for at
     * assembly, which is how many are present.
     */
    absentCount: (absent: number, total: number) =>
      `${total - absent} present · ${absent} absent · ${total} on the register`,
    /* 🔴 A WhatsApp message cannot be recalled, so this is on screen BEFORE the
       save rather than in a confirmation after it. */
    sendWarning:
      "Saving sends a WhatsApp message to the parent of each child newly marked absent. It cannot be undone.",
    /*
     * 🔴 THE HEADER GREETING, IN THE COPY TABLE RATHER THAN IN THE JSX.
     *
     * It was written inline as `Good morning, {teacherName || "Teacher"}` with
     * a waving-hand emoji beside it, and `Here's what's happening in
     * {className || "your class"} today.` — three English strings and two
     * fallbacks hardcoded into the component, in a file whose whole point is
     * that the tier's words live in one place.
     *
     * ⚠️ IT NO LONGER SAYS "GOOD MORNING". The teacher tier is used through the
     * school day — a register is taken at nine and reports are read at four —
     * and a greeting that is wrong after midday is worse than none. The
     * time-of-day version would need a clock, a timezone and a rule about when
     * afternoon starts, to say something the screen does not need to say.
     */
    greeting: (name: string) => (name ? `Hello, ${name}` : "Hello"),
    greetingSub: (className: string) =>
      className
        ? `Here's what's happening in ${className} today.`
        : "Here's what's happening today.",
    /** ⚠️ Used where a name is genuinely absent — never as a default parameter. */
    teacherFallback: "Teacher",

    navClasses: "Your classes",
    signOut: "Sign out",

    // Class picker
    pickClass: "Which class?",
    pickClassHint: "You teach more than one. Pick the class you are with now.",
    changeClass: "Change class",
    /** 🔴 Names what is wrong AND who fixes it — a teacher cannot fix this one. */
    noClass: "You are not assigned to a class yet.",
    noClassHint:
      "Ask your school admin to assign you to a class. Nothing here works until then.",

    /*
     * ✅ D3's LAUNCH WORKS NOW, SO THE TWO STRINGS THAT SAID IT DID NOT ARE
     * GONE. They read *"starting a game from here is not switched on yet"* and
     * explained that the gameplay write path would have to change first. It did
     * not have to: the child's own session is minted server-side and `KidApp`
     * plays as whoever that session names.
     */
    launching: "Starting…",
    /*
     * 🔴 THE CONFIRMATION NAMES THE CHILD **AND** THE ROLL NUMBER, which is
     * `launchConfirmLine`'s rule on D3 and matters more here: this launch has no
     * confirm screen in front of it, and two children called Aarav is the
     * ordinary case in an Indian classroom.
     */
    playNow: "Play",
    playConfirm: (name: string, roll: string | null) =>
      roll === null
        ? `Hand the laptop to ${name}?`
        : `Hand the laptop to ${name}? Everything they play saves to roll ${roll}.`,
    playConfirmYes: "Yes, hand it over",
    /* ⚠️ Named as a consequence, not as an action — the teacher is about to give
       away the screen they are reading. */
    playNowHint:
      "Starts a game as this child on this laptop. Their teacher dashboard records it under their roll number, exactly as if they had signed in themselves.",
    launchFailed: "Couldn’t start a game for this child.",

    /*
     * ── what they are actually playing ──────────────────────────────────────
     *
     * 🔴 IT IS NOT COVERAGE, AND THE HINT SAYS SO IN ONE LINE. Coverage answers
     * an inspector — which competencies has this class evidenced, where one
     * event counts. This answers the teacher standing in the room on a Tuesday.
     */
    gamesTitle: "What the class is playing",
    gamesHint:
      "Rounds finished, right answers and the level reached. A game with nothing against it has not been started — which is a different fact from a game going badly.",
    childGamesTitle: "What they have played",
    childGamesHint:
      "This child’s own rounds, right answers and level in each game. No comparison with anybody else.",

    // Between-screen navigation, once the fork has been taken.
    openLeaderboard: "Leaderboard",
    openReports: "Reports",
    backToDashboard: "Back to dashboard",
    backToStart: "Back",

    // States
    loadFailed: "Couldn’t load this.",
    /**
     * ⚠️ THIS USED TO SAY *"teacher sign-in isn't built yet"* AND THAT BECAME A
     * LIE IN §10.0. Staff login exists and works, so a 401/403 here is now a
     * ROLE or an expired session — and telling a teacher the product is
     * unfinished sends them to support for something they could fix by signing
     * in again.
     */
    signInMissing:
      "Your session has ended, or this account is not a teacher. Sign in again.",
    lastWorked: (at: string) => `Last loaded ${at}`,
    neverWorked: "It hasn’t loaded yet",
    retry: "Try again",
    emptyClass: "No children in this class yet.",
    importRoster: "Import a roster",

    /*
     * Page subtitles. Each says what the screen ANSWERS, not what it is called
     * — a heading that reads "Roster" over a subtitle that reads "The roster"
     * has spent a line saying nothing.
     */
    rosterHint:
      "Pick the child who is about to play. You will confirm before the game starts.",
    confirmHint:
      "Check the name and roll number — the session saves against this child.",
    overviewHint: "This week at a glance, and who to look at next.",
    leaderboardHint: "Coins, stickers and streaks across the class.",

    /*
     * 🔴 THE FOUR TILE LABELS, MOVED OUT OF THE MARKUP. They were literals
     * inside `TeacherScreens.tsx` — "Total coins", "Across the class",
     * "Keep it going!" — which is the one thing `GamesPanel` spends its whole
     * header forbidding: *"a component that emits its own copy is a component
     * whose wording no tier can correct."*
     */
    leaderboardTotalCoins: "Total coins",
    leaderboardCoinsCaption: "Across the class",
    leaderboardTotalStickers: "Total stickers",
    leaderboardStickersCaption: "Earned together",
    leaderboardBestStreak: "Best streak",
    leaderboardStreakCaption: "Days in a row",
    leaderboardActiveStudents: "Active students",
    /**
     * ⚠️ A FUNCTION, so the share is formatted where the words are. `formatDelta`
     * proved the alternative: a component that builds a sentence from a number
     * is a component the copy cannot reach.
     */
    leaderboardActiveCaption: (pct: number) => `${String(pct)}% have played`,
    reportsHint:
      "Which NCF-FS codes this class has covered, and where the gaps are.",
    /* `covered` / `uncovered` already exist above — D7 had them and rendered
       both groups into one undifferentiated row anyway. */
    byGame: "By game",
    when: "When",
    event: "Event",
    skill: "Skill",
  },
} as const;

/**
 * 🔴 THE ERROR MESSAGE NAMES THE REAL CAUSE WHEN WE KNOW IT.
 *
 * Every teacher screen 401s today, because staff login is Unit 9. "Couldn't
 * load this" would send whoever is testing the pilot build hunting a network
 * fault that does not exist. `DataSurface` already requires *what failed* and
 * *when it last worked*; this supplies the first.
 */
export function describeError(err: Error | null, lang: Lang): string {
  const t = TEACHER_COPY[lang];
  const status = (err as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return t.signInMissing;
  return t.loadFailed;
}
